import { supabase } from '@/integrations/supabase/client';
import { IAudioEngine, AudioState, AudioConfig, StateChangeCallback } from './IAudioEngine';

/**
 * Audio engine for pre-recorded MP3 files from database
 * Uses HTMLAudioElement for playback with full control support
 */
export class RecordedAudioEngine implements IAudioEngine {
  private audioElement: HTMLAudioElement | null = null;
  private state: AudioState = {
    isPlaying: false,
    isPaused: false,
    isStopped: true,
    currentTime: 0,
    duration: 0,
    progress: 0,
    currentWordIndex: 0,
    error: null,
  };
  private config: AudioConfig = {
    rate: 1,
    pitch: 1,
    volume: 0.8,
  };
  private callbacks: StateChangeCallback[] = [];
  private moduleId: string = '';
  private sectionId: string = '';
  private userId: string = '';
  private progressSaveTimer: number | null = null;

  /**
   * PHASE 4: Initialize with pre-fetched URL for instant playback
   * Uses existing Audio element if available from prefetch
   */
  async initializeWithUrl(audioUrl: string, moduleId: string, sectionId: string, config?: Partial<AudioConfig>) {
    console.log('🎵 RecordedAudioEngine: Fast initialization with pre-fetched URL');
    
    this.moduleId = moduleId;
    this.sectionId = sectionId;
    
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      this.userId = user.id;
    }

    // Create audio element - browser will use cached file if available
    this.audioElement = new Audio(audioUrl);
    this.audioElement.volume = this.config.volume;
    this.audioElement.playbackRate = this.config.rate;
    this.audioElement.preload = 'metadata'; // Fast metadata load

    // Set up event listeners
    this.setupAudioListeners();

    // Load last position from DB (async, non-blocking)
    this.loadLastPosition().catch(err => {
      console.warn('Failed to load last position:', err);
    });
  }

  async initialize(audioUrl: string, moduleId: string, sectionId: string, config?: Partial<AudioConfig>) {
    // Delegate to initializeWithUrl for consistency
    return this.initializeWithUrl(audioUrl, moduleId, sectionId, config);
  }

  private setupAudioListeners() {
    if (!this.audioElement) return;

    this.audioElement.addEventListener('loadedmetadata', () => {
      if (!this.audioElement) return;
      this.state.duration = this.audioElement.duration;
      this.notifyStateChange();
    });

    this.audioElement.addEventListener('timeupdate', () => {
      if (!this.audioElement) return;
      this.state.currentTime = this.audioElement.currentTime;
      this.state.progress = this.state.duration > 0 
        ? (this.state.currentTime / this.state.duration) * 100 
        : 0;
      this.notifyStateChange();
      
      // Throttled progress save
      this.scheduleSaveProgress();
    });

    this.audioElement.addEventListener('ended', () => {
      this.state.isPlaying = false;
      this.state.isPaused = false;
      this.state.isStopped = true;
      this.state.progress = 100;
      this.notifyStateChange();
      this.saveProgress(this.state.duration, 100);
    });

    this.audioElement.addEventListener('error', (e) => {
      console.error('🎵 RecordedAudioEngine: Audio error:', e);
      this.state.error = 'Failed to load audio file';
      this.state.isStopped = true;
      this.notifyStateChange();
    });

    this.audioElement.addEventListener('play', () => {
      this.state.isPlaying = true;
      this.state.isPaused = false;
      this.state.isStopped = false;
      this.notifyStateChange();
    });

    this.audioElement.addEventListener('pause', () => {
      if (!this.audioElement) return;
      // Only set isPaused if not at the end
      if (this.audioElement.currentTime < this.audioElement.duration) {
        this.state.isPaused = true;
        this.state.isPlaying = false;
      }
      this.notifyStateChange();
    });
  }

  async play(): Promise<void> {
    if (!this.audioElement) {
      console.warn('🎵 RecordedAudioEngine: No audio element');
      return;
    }
    
    try {
      await this.audioElement.play();
      console.log('🎵 RecordedAudioEngine: Playing');
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Play failed:', error);
      this.state.error = 'Failed to play audio';
      this.notifyStateChange();
    }
  }

  pause() {
    if (!this.audioElement) return;
    this.audioElement.pause();
    this.saveProgress(this.state.currentTime, this.state.progress);
    console.log('🎵 RecordedAudioEngine: Paused');
  }

  stop() {
    if (!this.audioElement) return;
    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.state.isPlaying = false;
    this.state.isPaused = false;
    this.state.isStopped = true;
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.notifyStateChange();
    console.log('🎵 RecordedAudioEngine: Stopped');
  }

  seekTo(time: number) {
    if (!this.audioElement) return;
    const clampedTime = Math.max(0, Math.min(time, this.state.duration));
    this.audioElement.currentTime = clampedTime;
    this.state.currentTime = clampedTime;
    this.state.progress = this.state.duration > 0 
      ? (clampedTime / this.state.duration) * 100 
      : 0;
    this.notifyStateChange();
    this.saveProgress(clampedTime, this.state.progress);
    console.log('🎵 RecordedAudioEngine: Seeked to', clampedTime);
  }

  setRate(rate: number) {
    const clampedRate = Math.max(0.5, Math.min(2, rate));
    this.config.rate = clampedRate;
    if (this.audioElement) {
      this.audioElement.playbackRate = clampedRate;
    }
    console.log('🎵 RecordedAudioEngine: Rate set to', clampedRate);
  }

  setVolume(volume: number) {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.config.volume = clampedVolume;
    if (this.audioElement) {
      this.audioElement.volume = clampedVolume;
    }
    console.log('🎵 RecordedAudioEngine: Volume set to', clampedVolume);
  }

  getState(): AudioState {
    return { ...this.state };
  }

  onStateChange(callback: StateChangeCallback) {
    this.callbacks.push(callback);
  }

  private notifyStateChange() {
    this.callbacks.forEach(cb => cb(this.getState()));
  }

  private async loadLastPosition() {
    if (!this.userId || !this.moduleId) return;

    try {
      const { data, error } = await supabase
        .from('audio_progress')
        .select('last_position, progress')
        .eq('user_id', this.userId)
        .eq('module_id', this.moduleId)
        .eq('section_id', this.sectionId)
        .maybeSingle();

      if (data && !error && this.audioElement) {
        const position = data.last_position || 0;
        if (position > 0 && position < this.state.duration) {
          this.audioElement.currentTime = position;
          this.state.currentTime = position;
          this.state.progress = data.progress || 0;
          this.notifyStateChange();
          console.log('🎵 RecordedAudioEngine: Resumed from', position);
        }
      }
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Failed to load progress:', error);
    }
  }

  private scheduleSaveProgress() {
    if (this.progressSaveTimer) {
      clearTimeout(this.progressSaveTimer);
    }
    
    // Save progress every 2 seconds
    this.progressSaveTimer = window.setTimeout(() => {
      this.saveProgress(this.state.currentTime, this.state.progress);
    }, 2000);
  }

  private async saveProgress(position: number, progress: number) {
    if (!this.userId || !this.moduleId) return;

    try {
      await supabase
        .from('audio_progress')
        .upsert({
          user_id: this.userId,
          module_id: this.moduleId,
          section_id: this.sectionId,
          last_position: position,
          progress: Math.min(100, Math.round(progress)),
          duration: this.state.duration,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,module_id,section_id'
        });
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Failed to save progress:', error);
    }
  }

  destroy() {
    if (this.progressSaveTimer) {
      clearTimeout(this.progressSaveTimer);
    }
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement = null;
    }
    this.callbacks = [];
    console.log('🎵 RecordedAudioEngine: Destroyed');
  }
}
